import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useState, useEffect, useMemo } from "react";
import { FilterMatchMode } from "primereact/api";
import { FiClock } from "react-icons/fi";
import { MdEdit, MdDelete, MdAdd } from "react-icons/md";
import ConfirmDialog from "../dialog/ConfirmDialog";
import FormDialog from "../dialog/FormDialog";
import Select from "react-select";

const TableSlotValues = ({
    data = [],
    onEditSlotValue,
    onDeleteSlotValue,
    onAddSlotValue,
    variables = []
}) => {
    const [slotsData, setSlotsData] = useState([]);
    const [expandedRows, setExpandedRows] = useState(null);
    const [slotSearchTerm, setSlotSearchTerm] = useState("");
    const [filters, setFilters] = useState({
        user_id: { value: null, matchMode: FilterMatchMode.CONTAINS },
        slots_search_text: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });

    const [deleteValueDialog, setDeleteValueDialog] = useState(false);
    const [editValueDialog, setEditValueDialog] = useState(false);

    const [newValueDialog, setNewValueDialog] = useState(false);
    const [newValue, setNewValue] = useState("");
    const [selectedVariable, setSelectedVariable] = useState(null);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [selectSlotValueId, setSelectSlotValueId] = useState(null);

    const handleCloseDialog = () => {
        setNewValue("");
        setSelectedVariable(null);
        setSelectedUserId(null);
        setSelectSlotValueId(null);
    };

    const variablesOptions = (variables || []).map((variable) => ({
        value: variable.id,
        label: variable.label || variable.name || variable.slot_name || `Variable ${variable.id}`,
    }));

    const selectedVariableOption = variablesOptions.find(
        (option) => option.value === selectedVariable
    ) || null;

    const handleSelectCandidate = (selectedOption) => {
        setSelectedVariable(selectedOption ? selectedOption.value : null);
    };

    useEffect(() => {
        setSlotsData([...data]);
    }, [data]);

    // Preparamos los datos agregando una propiedad string con todos los nombres y valores de los slots
    // Esto permite a PrimeReact filtrar filas buscando dentro de ese string acumulado.
    const processedSlotsData = useMemo(() => {
        return slotsData.map((item) => {
            const searchText = (item.slots || [])
                .map((slot) => `${slot.slot_name || ""} ${slot.slot_value || ""}`)
                .join(" ");
            return {
                ...item,
                slots_search_text: searchText,
            };
        });
    }, [slotsData]);

    const getSlotsByUserId = (userId) => {
        const userSlots = slotsData.find(item => item.user_id === userId);
        return userSlots?.slots || [];
    };

    const formatDate = (updatedAt) => {
        if (!updatedAt) return "-";
        return `${updatedAt.date} ${updatedAt.time}`;
    };

    const highlightMatch = (text, searchTerm) => {
        const value = text ?? "";
        const term = (searchTerm || "").trim();

        if (!term || !value) {
            return value || "-";
        }

        const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const regex = new RegExp(`(${escapedTerm})`, "gi");
        const parts = String(value).split(regex);

        return parts.map((part, index) => {
            if (part.toLowerCase() === term.toLowerCase()) {
                return (
                    <mark
                        key={`${part}-${index}`}
                        style={{
                            backgroundColor: "rgba(255, 215, 0, 0.35)",
                            color: "inherit",
                            padding: 0,
                        }}
                    >
                        {part}
                    </mark>
                );
            }

            return <span key={`${part}-${index}`}>{part}</span>;
        });
    };

    const isRowExpanded = (row) => {
        if (!expandedRows) return false;

        if (Array.isArray(expandedRows)) {
            return expandedRows.some((expandedRow) => expandedRow.user_id === row.user_id);
        }

        return Boolean(expandedRows[row.user_id]);
    };

    const rowExpansionTemplate = (row) => {
        const slots = getSlotsByUserId(row.user_id);

        return (
            <div className="px-3 py-3" style={{ backgroundColor: "var(--card-tertiary-color)", borderRadius: "15px" }}>
                <div className="row row-cols-1 row-cols-md-3 g-3">
                    {slots.map((slot) => {
                        return (
                            <div key={slot.id} className="col">
                                <div
                                    className="card h-100 p-2"
                                    style={{
                                        backgroundColor: "var(--card-color)",
                                        borderColor: "var(--separator-color)"
                                    }}
                                >
                                    <div className="pb-2" style={{ borderBottom: "1px solid var(--separator-color)" }}>
                                        <div className="d-flex justify-content-between align-items-center gap-2">
                                            <div className="text-truncate flex-grow-1 min-w-0">
                                                <span
                                                    className="fw-bold text-uppercase text-truncate flex-shrink-0"
                                                    style={{ fontSize: "11px", color: "var(--text-color, inherit)" }}
                                                >
                                                    {highlightMatch(slot.slot_name, slotSearchTerm)}
                                                </span>
                                            </div>

                                            <div className="d-flex gap-1 flex-shrink-0">
                                                <span
                                                    role="button"
                                                    tabIndex={0}
                                                    title="Editar slot"
                                                    onClick={() => {
                                                        setSelectedUserId(row.user_id);
                                                        setSelectSlotValueId(slot.id);
                                                        setNewValue(slot.slot_value || "");
                                                        setEditValueDialog(true);
                                                    }}
                                                    style={{ color: "var(--text-color, inherit)", cursor: "pointer" }}
                                                >
                                                    <MdEdit size={14} />
                                                </span>
                                                <span
                                                    role="button"
                                                    tabIndex={0}
                                                    title="Eliminar slot"
                                                    onClick={() => {
                                                        setSelectedUserId(row.user_id);
                                                        setSelectSlotValueId(slot.id);
                                                        setDeleteValueDialog(true);
                                                    }}
                                                    style={{ color: "var(--text-color, inherit)", cursor: "pointer" }}
                                                >
                                                    <MdDelete size={14} />
                                                </span>
                                            </div>
                                        </div>

                                        <div className="pt-2">
                                            <div
                                                className="text-truncate"
                                                style={{ fontSize: "12px", color: "var(--text-color, inherit)" }}
                                            >
                                                {highlightMatch(slot.slot_value || "—", slotSearchTerm)}
                                            </div>
                                        </div>
                                    </div>

                                    <div
                                        className="d-flex align-items-center gap-1 pt-2"
                                        style={{
                                            fontSize: "10px",
                                            color: "var(--text-color-secondary, var(--bs-secondary-color))"
                                        }}
                                    >
                                        <FiClock size={10} />
                                        <span>
                                            {slot.updated_at?.date || "—"} - {slot.updated_at?.time || "—"} hs
                                        </span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    <div className="col">
                        <div
                            role="button"
                            tabIndex={0}
                            onClick={() => {
                                setSelectedUserId(row.user_id);
                                setNewValueDialog(true);
                            }}
                            className="card h-100 p-3 d-flex flex-column align-items-center justify-content-center text-center user-select-none"
                            style={{
                                backgroundColor: "transparent",
                                borderColor: "var(--separator-color)",
                                borderStyle: "dashed",
                                borderWidth: "2px",
                                cursor: "pointer",
                                minHeight: "100px",
                                transition: "all 0.2s ease-in-out"
                            }}
                            title="Agregar nuevo valor"
                        >
                            <div
                                className="rounded-circle d-flex align-items-center justify-content-center mb-1"
                                style={{
                                    width: "36px",
                                    height: "36px",
                                    backgroundColor: "var(--card-color)",
                                    color: "var(--primary-color, var(--bs-primary))",
                                    border: "1px solid var(--separator-color)"
                                }}
                            >
                                <MdAdd size={20} />
                            </div>
                            <span
                                className="fw-semibold"
                                style={{ fontSize: "11px", color: "var(--text-color)" }}
                            >
                                AGREGAR
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const getUserSlotsPreview = (userId) => {
        const slots = getSlotsByUserId(userId);
        if (!slots.length) return "-";

        const previewSlots = slots.slice(0, 2);
        const hiddenCount = slots.length - previewSlots.length;

        return (
            <div style={{ minWidth: "250px" }}>
                {previewSlots.map((slot) => (
                    <div
                        key={slot.id}
                        className="mb-1 pb-1"
                        style={{
                            fontSize: "12px",
                            color: "var(--text-color)",
                            borderBottom: "1px solid var(--separator-color)",
                        }}
                    >
                        <strong>{highlightMatch(slot.label || slot.slot_name, slotSearchTerm)}:</strong> {" "}
                        {highlightMatch(slot.slot_value || "-", slotSearchTerm)}
                    </div>
                ))}
                {hiddenCount > 0 && (
                    <div className="mt-1" style={{ fontSize: "12px", color: "var(--text-color-secondary)" }}>
                        +{hiddenCount} slot{hiddenCount > 1 ? "s" : ""}. Expande la fila para ver todo.
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <div className="card overflow-hidden" aria-label="table" >
                <DataTable
                    value={processedSlotsData}
                    filters={filters}
                    dataKey="user_id"
                    expandedRows={expandedRows}
                    onRowToggle={(e) => setExpandedRows(e.data)}
                    onFilter={(e) => {
                        setFilters(e.filters);
                        const term = e.filters?.slots_search_text?.value || "";
                        setSlotSearchTerm(term);
                    }}
                    rowClassName={(row) => (isRowExpanded(row) ? "slot-row-active" : "")}
                    rowStyle={(row) =>
                        isRowExpanded(row)
                            ? {
                                boxShadow: "inset 3px 0 0 var(--primary-color)",
                                transition: "background-color 0.2s ease"
                            }
                            : null
                    }
                    rowExpansionTemplate={rowExpansionTemplate}
                    paginator
                    rows={10}
                    rowsPerPageOptions={[5, 10, 25]}
                    filterDisplay="row"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords}"
                    filter
                    responsiveLayout="scroll"
                    aria-label="slot-values"
                >
                    <Column expander style={{ width: "3rem" }} />
                    <Column
                        field="user_id"
                        header="ID de Usuario"
                        filter
                        filterMatchMode="contains"
                        showFilterMenu={false}
                        sortable
                    />
                    <Column
                        field="slots_search_text"
                        header="Variables y Valores"
                        filter
                        filterMatchMode="contains"
                        showFilterMenu={false}
                        filterPlaceholder="Buscar variable o valor..."
                        body={(row) => getUserSlotsPreview(row.user_id)}
                    />
                    <Column
                        header="Última Actualización"
                        body={(row) => {
                            const slots = getSlotsByUserId(row.user_id);
                            if (!slots.length) return "-";

                            const lastUpdate = slots.reduce((latest, current) => {
                                return (current.updated_at?.timestamp || 0) > (latest.updated_at?.timestamp || 0)
                                    ? current
                                    : latest;
                            });

                            return (
                                <div style={{ fontSize: "12px" }}>
                                    <div style={{ color: "var(--text-color)" }}>
                                        {formatDate(lastUpdate.updated_at)}
                                    </div>
                                    <div className="mt-1" style={{ color: "var(--text-color-secondary)" }}>
                                        {lastUpdate.updated_at?.relative}
                                    </div>
                                </div>
                            );
                        }}
                    />
                </DataTable>
                <style>
                    {`
                    .slot-row-active > td:first-child {
                        box-shadow: inset 3px 0 0 var(--primary-color);
                    }
                `}
                </style>
            </div>

            <FormDialog
                visible={newValueDialog}
                title="Agregar Nuevo Valor"
                acceptDialog={() => {
                    onAddSlotValue(selectedUserId, selectedVariable, newValue);
                    setNewValueDialog(false);
                    handleCloseDialog();
                }}
                closeDialog={() => {
                    setNewValueDialog(false);
                    handleCloseDialog();
                }}
            >
                <div className="d-flex flex-column gap-3">
                    <div className="field">
                        <label className={"ms-3"}>
                            Variable
                        </label>
                        <Select
                            classNamePrefix="react-select"
                            options={variablesOptions}
                            value={selectedVariableOption}
                            menuPosition="fixed"
                            onChange={handleSelectCandidate}
                            noOptionsMessage={() => "No se ha registrado ninguna variable."}
                            placeholder="Selecciona una variable.."
                        />
                    </div>

                    <div className="field">
                        <label className={"ms-3"}>
                            Valor
                        </label>
                        <input
                            value={newValue}
                            onChange={(e) => {
                                setNewValue(e.target.value);
                            }}
                            maxLength={300}
                            placeholder="Ingrese el valor.."
                        />
                    </div>
                </div>
            </FormDialog>

            <FormDialog
                visible={editValueDialog}
                title="Editar Valor"
                acceptDialog={() => {
                    onEditSlotValue(selectSlotValueId, selectedUserId, newValue);
                    setEditValueDialog(false);
                    handleCloseDialog();
                }}
                closeDialog={() => {
                    setEditValueDialog(false);
                    handleCloseDialog();
                }}
            >
                <div className="d-flex flex-column gap-3">
                    <div className="field">
                        <label className={"ms-3"}>
                            Valor
                        </label>
                        <input
                            value={newValue}
                            onChange={(e) => {
                                setNewValue(e.target.value);
                            }}
                            maxLength={300}
                            placeholder="Ingrese el valor.."
                        />
                    </div>
                </div>
            </FormDialog>

            <ConfirmDialog
                visible={deleteValueDialog}
                title="Eliminar Valor"
                onClose={() => {
                    setDeleteValueDialog(false);
                }}
                icon={<MdDelete size={40} />}
                iconBackgroundColor="red"
                confirmColor="danger"
                onConfirm={() => {
                    onDeleteSlotValue(selectSlotValueId, selectedUserId);
                    setDeleteValueDialog(false);
                    handleCloseDialog();
                }}
                description="¿Estás seguro que quieres eliminar el valor?"
            />
        </>
    );
};

export default TableSlotValues;